import { useEffect } from "react";

/**
 * Lightweight SEO helper — sets <title>, meta description, canonical, OG/Twitter,
 * and an optional schema.org JSON-LD block directly on document.head.
 * Cleans up its own tags on unmount so per-page metadata stays accurate for SPA SEO.
 */
export function useSeo({
  title,
  description,
  canonicalPath = "/",
  ogImage = "https://customer-assets.emergentagent.com/job_shaji-nada-producer/artifacts/7zssbowd_Gemini_Generated_Image_e3amy7e3amy7e3adm.png",
  jsonLd = null,
} = {}) {
  useEffect(() => {
    const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
    const url = `${baseUrl}${canonicalPath}`;

    if (title) document.title = title;

    const setMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs.create || {}).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      Object.entries(attrs.set || {}).forEach(([k, v]) => el.setAttribute(k, v));
      return el;
    };

    setMeta('meta[name="description"]', { create: { name: "description" }, set: { content: description || "" } });

    // canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    setMeta('meta[property="og:title"]', { create: { property: "og:title" }, set: { content: title || "" } });
    setMeta('meta[property="og:description"]', { create: { property: "og:description" }, set: { content: description || "" } });
    setMeta('meta[property="og:url"]', { create: { property: "og:url" }, set: { content: url } });
    setMeta('meta[property="og:image"]', { create: { property: "og:image" }, set: { content: ogImage } });
    setMeta('meta[property="og:type"]', { create: { property: "og:type" }, set: { content: "website" } });
    setMeta('meta[name="twitter:card"]', { create: { name: "twitter:card" }, set: { content: "summary_large_image" } });
    setMeta('meta[name="twitter:title"]', { create: { name: "twitter:title" }, set: { content: title || "" } });
    setMeta('meta[name="twitter:description"]', { create: { name: "twitter:description" }, set: { content: description || "" } });
    setMeta('meta[name="twitter:image"]', { create: { name: "twitter:image" }, set: { content: ogImage } });

    // JSON-LD
    let script = document.getElementById("seo-jsonld");
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.id = "seo-jsonld";
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }
  }, [title, description, canonicalPath, ogImage, jsonLd]);
}
